import React from 'react'
import { Shield, Lock, Server, Eye, Key, CheckCircle } from 'lucide-react'
import { usePageMeta } from '@/hooks/usePageMeta'

export default function Security() {
  usePageMeta({
    title: 'Security | Claritas',
    description:
      'Claritas uses Stripe for bank-level security across payments and financial connections. 256-bit AES encryption, SOC 2 Type II certified, PCI DSS Level 1 compliant—your financial data is always protected.',
  })
  return (
    <div className="min-h-screen bg-background-light">

      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Security
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Built on Trust
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
            Claritas takes the security of your financial data seriously. We partner with industry
            leaders to ensure every transaction and every piece of personal information is protected
            at every step.
          </p>
        </div>
      </section>

      {/* Security Partners */}
      <section className="bg-background-light py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Trusted Partners
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Industry-Standard Infrastructure
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              We rely on proven, audited platforms to handle your financial data—so you never have to worry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-8 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-xl font-bold font-display text-charcoal mb-3">Stripe Financial Connections for Bank Connectivity</h3>
              <p className="text-sm text-charcoal-muted mb-4">
                Bank account connections are handled exclusively through Stripe Financial Connections.
                Claritas never stores your banking credentials—Stripe manages the secure connection
                directly.
              </p>
              <ul className="space-y-2">
                {['256-bit AES encryption', 'SOC 2 Type II certified', 'GDPR and CCPA compliant', 'OAuth-based access'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-charcoal-muted">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Lock className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-xl font-bold font-display text-charcoal mb-3">Stripe for Payments</h3>
              <p className="text-sm text-charcoal-muted mb-4">
                All donation transactions are processed through Stripe, the global standard for online
                payments. Card and bank transfer data is handled within Stripe's PCI-compliant
                environment—never on our servers.
              </p>
              <ul className="space-y-2">
                {['PCI DSS Level 1 certified', 'Tokenized payment data', 'Fraud detection and monitoring', 'Secure direct deposits'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-charcoal-muted">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Practices */}
      <section className="bg-white py-20 border-t border-surface-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Our Practices
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Security at Every Layer
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              Beyond our third-party partners, Claritas implements rigorous security practices across
              our entire platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Key className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Encrypted at Rest</h3>
              <p className="text-sm text-charcoal-muted">
                All user data stored on our servers is encrypted at rest using AES-256, the same standard
                used by banks and government agencies.
              </p>
            </div>

            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Server className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Encrypted in Transit</h3>
              <p className="text-sm text-charcoal-muted">
                Every connection to Claritas is secured with TLS 1.3. Your data is protected from
                interception every time you use the platform.
              </p>
            </div>

            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Eye className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">No Credential Storage</h3>
              <p className="text-sm text-charcoal-muted">
                We never store your bank login credentials. Authentication with your financial institution
                is handled entirely by Stripe Financial Connections' secure tokenization.
              </p>
            </div>

            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Authentication</h3>
              <p className="text-sm text-charcoal-muted">
                User accounts are protected by Supabase Auth, providing secure password hashing,
                session management, and support for multi-factor authentication.
              </p>
            </div>

            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Lock className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Role-Based Access</h3>
              <p className="text-sm text-charcoal-muted">
                Donors, parish administrators, and platform admins operate within strict permission
                boundaries. Sensitive operations require appropriate authorization.
              </p>
            </div>

            <div className="bg-background-light p-6 border border-surface-border rounded hover:border-gold/30 transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <CheckCircle className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Transparent Audit Trail</h3>
              <p className="text-sm text-charcoal-muted">
                Every transaction is logged and traceable. Donors can review their giving history and
                parishes can audit all received contributions at any time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Note */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Your Privacy
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
            We Do Not Sell Your Data
          </h2>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
            Your personal and financial information is used solely to operate the Claritas platform.
            We do not sell, share, or monetize your data in any way. Your faith, your finances, and
            your generosity are private.
          </p>
        </div>
      </section>
    </div>
  )
}
